/**
 * GooglePlacesInput — autocomplete via Google Places API New (Text Search).
 *
 * Usage:
 *   <GooglePlacesInput value={address} onSelect={({address, city, country, place_id, lat, lng}) => ...} />
 *
 * Falls back to a plain input if REACT_APP_GOOGLE_PLACES_API_KEY is missing.
 */
import { useEffect, useRef, useState } from "react";
import { MapPin, Search } from "lucide-react";

const KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

export default function GooglePlacesInput({ value = "", onSelect, placeholder = "Adresse…", testId = "places-input" }) {
  const [q, setQ] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => setQ(value), [value]);
  useEffect(() => {
    if (!q.trim() || q === value || !KEY) { setSuggestions([]); return; }
    const ctrl = new AbortController();
    const tmo = setTimeout(async () => {
      try {
        const r = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Goog-Api-Key": KEY },
          body: JSON.stringify({ input: q, languageCode: "fr" }),
          signal: ctrl.signal,
        });
        const data = await r.json();
        setSuggestions((data.suggestions || []).slice(0, 6));
        setOpen(true);
      } catch (e) { /* ignore */ }
    }, 250);
    return () => { clearTimeout(tmo); ctrl.abort(); };
  }, [q, value]);

  useEffect(() => {
    const handle = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const pick = async (suggestion) => {
    const pid = suggestion.placePrediction?.placeId;
    const text = suggestion.placePrediction?.text?.text || suggestion.placePrediction?.structuredFormat?.mainText?.text || "";
    setQ(text); setOpen(false);
    if (!pid) { onSelect?.({ address: text, place_id: "" }); return; }
    try {
      const r = await fetch(`https://places.googleapis.com/v1/places/${pid}`, {
        headers: { "X-Goog-Api-Key": KEY, "X-Goog-FieldMask": "id,displayName,formattedAddress,addressComponents,location" },
      });
      const d = await r.json();
      const comp = (type) => (d.addressComponents || []).find((c) => (c.types || []).includes(type))?.longText || "";
      onSelect?.({
        address: d.formattedAddress || text,
        location_name: d.displayName?.text || "",
        city: comp("locality") || comp("postal_town") || comp("administrative_area_level_2") || "",
        country: comp("country") || "",
        place_id: d.id || pid,
        lat: d.location?.latitude,
        lng: d.location?.longitude,
      });
    } catch (e) {
      onSelect?.({ address: text, place_id: pid });
    }
  };

  if (!KEY) {
    return (
      <input value={q} onChange={(e) => { setQ(e.target.value); onSelect?.({ address: e.target.value, place_id: "" }); }} placeholder={placeholder} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid={testId} />
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => suggestions.length && setOpen(true)} placeholder={placeholder} className="w-full rounded-xl border border-neutral-200 bg-white pl-10 pr-3 py-2 text-sm focus:border-wine-400 focus:outline-none" data-testid={testId} />
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl">
          {suggestions.map((s, i) => (
            <button key={i} type="button" onClick={() => pick(s)} className="block w-full border-b border-neutral-50 px-3 py-2 text-left text-xs hover:bg-wine-50" data-testid={`${testId}-suggestion-${i}`}>
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-wine-500" />
                <div className="min-w-0">
                  <div className="font-semibold text-neutral-900 truncate">{s.placePrediction?.structuredFormat?.mainText?.text || ""}</div>
                  <div className="text-[10px] text-neutral-500 truncate">{s.placePrediction?.structuredFormat?.secondaryText?.text || s.placePrediction?.text?.text || ""}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
